import { z } from "zod";
import { SECTION_CATALOG, SectionGrantsSchema } from "./section-access.js";
// ИИ-агенты сотрудников в Вехе по MCP (Паша 20.10.2026): руководитель выдаёт сотруднику часть Вехи на странице «Доступ»,
// сотрудник сам выбирает, какую часть своих прав получает его агент. Агент действует от имени сотрудника теми же
// маршрутами, что кнопки интерфейса; права агента — пересечение выбранного и текущих прав сотрудника на каждом запросе.
/** Sections an agent is never given: who may do what is decided by people on «Доступ». */
export const AGENT_EXCLUDED_SECTIONS = ["access"];
export const AgentConnectionGrantsSchema = SectionGrantsSchema
    .refine(grants => grants.every(grant => !AGENT_EXCLUDED_SECTIONS.includes(grant.section)), { message: "Раздел «Доступ» агенту не выдаётся." })
    .refine(grants => grants.length > 0, { message: "Выберите хотя бы один раздел." });
export const AgentConnectionSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    clientName: z.string().min(1).max(120),
    redirectHost: z.string().min(1).max(255),
    grants: SectionGrantsSchema,
    createdAt: z.string().datetime({ offset: true }),
    lastUsedAt: z.string().datetime({ offset: true }).nullable(),
    /** Whose agent it is: filled in the owner's and administrator's list of the whole team. */
    employee: z.string().min(1).max(200).optional(),
}).strict();
export const AgentConnectionsOverviewSchema = z.object({
    connections: z.array(AgentConnectionSchema).max(200),
    /** The other employees' agents, for an owner or administrator (they may disconnect any); empty for everyone else. */
    team: z.array(AgentConnectionSchema).max(500),
    /** The address an agent is given (Claude: «Настройки → Коннекторы → Добавить свой»). */
    mcpUrl: z.string().url(),
}).strict();
/** The OAuth request the agent sent the browser with, as the consent page received it. */
export const AgentAuthorizationRequestSchema = z.object({
    clientId: z.string().regex(/^vk_[A-Za-z0-9_-]{22}$/u),
    redirectUri: z.string().url().max(2000),
    codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    codeChallengeMethod: z.literal("S256"),
    state: z.string().max(2000).optional(),
}).strict();
export const AgentAuthorizationPreviewSchema = z.object({
    clientName: z.string(),
    redirectHost: z.string(),
    /** What this employee has now: the agent can be given these or less. */
    available: SectionGrantsSchema,
}).strict();
export const AgentAuthorizationApprovalSchema = AgentAuthorizationRequestSchema.extend({
    name: z.string().trim().min(1).max(120),
    grants: AgentConnectionGrantsSchema,
}).strict();
export const AgentAuthorizationResultSchema = z.object({ redirectTo: z.string().url() }).strict();
const LEVEL_RANK = { read: 1, edit: 2, full: 3 };
const lower = (a, b) => LEVEL_RANK[a] <= LEVEL_RANK[b] ? a : b;
const INCLUDED_IN = new Map(SECTION_CATALOG.flatMap(section => section.includedIn ? [[section.key, section.includedIn]] : []));
const LEVELS = new Map(SECTION_CATALOG.map(section => [section.key, section.levels]));
/** «Реклама» by cabinet (20.10.2026): the cabinets both sides allow — undefined is every cabinet; an empty list is none. */
const bothCabinets = (want, own) => own === undefined ? want : want === undefined ? [...own] : want.filter(cabinet => own.includes(cabinet));
/** Everything a person may give an agent: their own grants, or for an owner or administrator (no restriction) every
 * section at its highest level. «Доступ» is never among them. */
export function agentAvailableGrants(access) {
    const grants = access ? access.grants : SECTION_CATALOG.map((section) => ({ section: section.key, level: section.levels[section.levels.length - 1], scope: "all" }));
    return grants.filter(grant => !AGENT_EXCLUDED_SECTIONS.includes(grant.section));
}
/**
 * The agent's rights now: what the employee chose for it, cut to what the employee has at this moment. A permission taken
 * from the employee is taken from the agent at the next request; one given to the employee later is not added. Calendar
 * records keep the employee's scope (own records stay own). `null` access is an owner or administrator: the chosen grants.
 */
export function agentEffectiveAccess(access, chosen) {
    const result = new Map();
    const put = (grant) => {
        if (AGENT_EXCLUDED_SECTIONS.includes(grant.section))
            return;
        const levels = LEVELS.get(grant.section) ?? [];
        if (!levels.includes(grant.level))
            return;
        if (grant.cabinets && grant.cabinets.length === 0)
            return;
        const known = result.get(grant.section);
        // Of two equal levels the wider wins: every cabinet over some of them (the employee's «Вся аналитика» beside «Реклама: Илья»).
        if (!known || LEVEL_RANK[grant.level] > LEVEL_RANK[known.level] || (grant.level === known.level && known.cabinets && !grant.cabinets))
            result.set(grant.section, grant);
    };
    for (const want of chosen) {
        if (!access) {
            put(want);
            continue;
        }
        // The same section, or the wider permission that opens it («Вся аналитика» opens «Рекламу»).
        const own = access.grants.filter(grant => grant.section === want.section || grant.section === INCLUDED_IN.get(want.section));
        for (const grant of own) {
            const scoped = grant.scope === "own" ? { scope: "own", ...(grant.ownType ? { ownType: grant.ownType } : {}) } : { scope: want.scope, ...(want.ownType ? { ownType: want.ownType } : {}) };
            // An agent never sees more ad cabinets than the employee: of «Реклама» — the cabinets of both (20.10).
            const cabinets = want.section === "analytics_ads" ? bothCabinets(want.cabinets, grant.section === "analytics_ads" ? grant.cabinets : undefined) : undefined;
            put({ section: want.section, level: lower(want.level, grant.level), ...scoped, ...(cabinets ? { cabinets } : {}) });
        }
        // A wider permission chosen while the employee now has only some of what it opens: those, not more.
        if (own.length === 0) {
            for (const grant of access.grants)
                if (INCLUDED_IN.get(grant.section) === want.section)
                    put({ ...grant, level: lower(want.level, grant.level) });
        }
    }
    return { mode: "personal", groupId: null, grants: [...result.values()], managerId: access?.managerId ?? null };
}
//# sourceMappingURL=agent-connections.js.map